import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './d-issue-message-thread.js';
import { t } from 'src/i18n';
import { fetchIssueMessages, IssueClosedError, postIssueMessage } from './issue-messages-client.js';
import type { IssueMessage } from './issue-message.js';

/**
 * Behandlerens meldingstråd «i avviket». Vises kun for anonyme avvik. Henter
 * meldinger on-demand og sender via messages-endepunktet (backend setter author +
 * varsler melder). `closed` styres av avvikets behandlingsstatus.
 */
@customElement('d-issue-messages-section')
export class DIssueMessagesSection extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      margin-top: 12px;
      border: 1px solid var(--borderColor, #d6d3cc);
      border-radius: 8px;
      padding: 16px 20px;
      background: hsl(0, 0%, 99%);
    }
    h3 {
      margin: 0 0 12px;
      font-size: 15px;
      color: var(--pageHeaderColor, #1a1a1a);
    }
    .error {
      font-size: 13px;
      color: hsl(0, 60%, 40%);
      margin-bottom: 10px;
    }
  `;

  @property({ type: String })
  organizationId = '';

  @property({ type: String })
  issueId = '';

  @property({ type: Boolean })
  anonymous = false;

  @property({ type: Boolean })
  closed = false;

  @state()
  private messages: IssueMessage[] = [];

  @state()
  private error = '';

  private loadStarted = false;

  protected updated(changed: PropertyValues): void {
    super.updated(changed);
    if (!this.loadStarted && this.anonymous && this.organizationId !== '' && this.issueId !== '') {
      this.loadStarted = true;
      void this.load();
    }
  }

  private async load(): Promise<void> {
    try {
      this.messages = await fetchIssueMessages(this.organizationId, this.issueId);
    } catch {
      this.error = t('issues.msgLoadError');
    }
  }

  private async onSend(body: string): Promise<void> {
    try {
      await postIssueMessage(this.organizationId, this.issueId, body);
      this.messages = await fetchIssueMessages(this.organizationId, this.issueId);
      this.error = '';
    } catch (e) {
      if (e instanceof IssueClosedError) {
        this.closed = true;
        this.error = '';
      } else {
        this.error = t('issues.msgSendError');
      }
    }
  }

  render() {
    if (!this.anonymous) return nothing;
    return html`
      <h3>${t('issues.msgSectionTitle')}</h3>
      ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      <d-issue-message-thread
        .messages=${this.messages}
        .viewer=${'handler'}
        ?closed=${this.closed}
        @send=${(e: CustomEvent<{ body: string }>) => this.onSend(e.detail.body)}
      ></d-issue-message-thread>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-messages-section': DIssueMessagesSection;
  }
}
